import { useState, useEffect, useCallback } from "react"
import { useParams, useLocation, useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import BarChart from "../../components/analytics/BarChart"
import DonutChart from "../../components/analytics/DonutChart"
import InstagramEmbed from "../../components/analytics/InstagramEmbed"
import InstagramLinkButton from "../../components/analytics/InstagramLinkButton"
import PostCategorySelector from "../../components/analytics/PostCategorySelector"
import { api } from "../../lib/api"
import type { PostDetail } from "../../types/analytics"
import { useSelectedAccount } from "../../contexts/AccountContext"
import "../../styles/analytics.scss"

const toStatItem = (v: number | null) => ({ value: v, change_rate: null })

const fmt = (v: number | null) => v != null ? v.toLocaleString() : "—"

const fmtMs = (ms: number | null) => {
  if (ms == null) return "—"
  const sec = Math.round(ms / 1000)
  return sec >= 60 ? `${Math.floor(sec / 60)}分${sec % 60}秒` : `${sec}秒`
}

const formatDate = (iso: string | null) => {
  if (!iso) return "—"
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

const ReelDetailPage = () => {
  const { postId } = useParams<{ postId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedAccountIds, currentPermission, setSingleAccountOnly } = useSelectedAccount()
  const [pinnedAccountId, setPinnedAccountId] = useState<number | null>(
    (location.state as { accountId?: number } | null)?.accountId ?? null
  )

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  useEffect(() => {
    if (pinnedAccountId === null && selectedAccountIds.length > 0) {
      setPinnedAccountId(selectedAccountIds[0])
    }
  }, [selectedAccountIds, pinnedAccountId])

  const selectedAccountId = (pinnedAccountId !== null && selectedAccountIds.includes(pinnedAccountId))
    ? pinnedAccountId
    : (selectedAccountIds[0] ?? null)

  const [post, setPost]       = useState<PostDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (selectedAccountId === null || !postId) { setLoading(false); return }
    setLoading(true)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.get<PostDetail>(`/analytics/posts/${postId}?${params}`)
      setPost(res)
    } catch {
      // エラーは無視
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId, postId])

  useEffect(() => { fetchData() }, [fetchData])

  const a = post?.analytic

  const engagementBreakdown = a && (a.likes != null || a.comments != null || a.saves != null || a.shares != null)
    ? {
        labels: ["いいね", "コメント", "保存", "シェア"],
        datasets: [{ data: [a.likes ?? 0, a.comments ?? 0, a.saves ?? 0, a.shares ?? 0] }],
      }
    : null

  return (
    <AppLayout pageTitle="リール詳細" onBack={() => navigate(-1)}>
      <div className="analytics">
        {loading ? (
          <p style={{ color: colors.gray400 }}>読み込み中…</p>
        ) : !post ? (
          <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
        ) : (
          <div className="feed-post-detail">
            <div className="feed-post-detail__left">
              <div className="feed-post-detail__embed-card">
                <InstagramEmbed
                  permalink={post.permalink}
                  thumbnailUrl={post.thumbnail_url}
                  caption={post.caption}
                  available={post.available}
                  mediaItems={post.media_items}
                />
              </div>
              <div className="feed-post-detail__meta-card">
                <span className="feed-post-detail__date">{formatDate(post.posted_at)}</span>
                {post.caption && (
                  <p className="feed-post-detail__caption">{post.caption}</p>
                )}
                {post.hashtags.length > 0 && (
                  <div className="feed-post-detail__hashtags">
                    {post.hashtags.map(tag => (
                      <span key={tag} className="feed-post-detail__hashtag">{tag}</span>
                    ))}
                  </div>
                )}
                {post.permalink && (
                  <>
                    <InstagramLinkButton href={post.available ? post.permalink : undefined} />
                    {!post.available && (
                      <p className="feed-post-detail__unavailable-notice">
                        <i className="bi bi-eye-slash" />
                        この投稿は現在、非公開または削除されています
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="feed-post-detail__right">

              {selectedAccountId && (
                <PostCategorySelector
                  postId={post.id}
                  accountId={selectedAccountId}
                  initialCategoryId={post.category_id ?? null}
                  initialCategoryName={post.category_name ?? null}
                  readOnly={currentPermission !== "edit"}
                />
              )}

              <div className="analytics__stats-grid">
                <StatCard label="再生数"              stat={toStatItem(a?.plays ?? null)} />
                <StatCard label="いいね"              stat={toStatItem(a?.likes ?? null)} />
                <StatCard label="コメント"             stat={toStatItem(a?.comments ?? null)} />
                <StatCard label="シェア" stat={toStatItem(a?.shares ?? null)} />
              </div>

              <div className="analytics__charts-grid">
                <div className="chart-card">
                  <div className="chart-card__header">
                    <span className="chart-card__title">再生数推移</span>
                  </div>
                  <div className="chart-card__canvas-wrap">
                    {post.trend
                      ? <BarChart data={post.trend} />
                      : <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                    }
                  </div>
                </div>

                <div className="chart-card">
                  <div className="chart-card__header">
                    <span className="chart-card__title">エンゲージメント内訳</span>
                  </div>
                  <div className="chart-card__canvas-wrap">
                    {engagementBreakdown
                      ? <DonutChart data={engagementBreakdown} />
                      : <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                    }
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">詳細指標</span>
                </div>
                <table className="post-detail__meta-table">
                  <tbody>
                    <tr>
                      <td>保存</td>
                      <td>{fmt(a?.saves ?? null)}</td>
                    </tr>
                    <tr>
                      <td>リーチ</td>
                      <td>{fmt(a?.reach ?? null)}</td>
                    </tr>
                    <tr>
                      <td>プロフィールアクセス</td>
                      <td>{fmt(a?.profile_visits ?? null)}</td>
                    </tr>
                    <tr>
                      <td>エンゲージメント率</td>
                      <td>{a?.engagement_rate != null ? `${a.engagement_rate}%` : "—"}</td>
                    </tr>
                    <tr>
                      <td>平均視聴時間</td>
                      <td>{fmtMs(a?.avg_watch_time ?? null)}</td>
                    </tr>
                    <tr>
                      <td>完全視聴率</td>
                      <td>{a?.full_play_rate != null ? `${a.full_play_rate}%` : "—"}</td>
                    </tr>
                    <tr>
                      <td>リプレイ</td>
                      <td>{fmt(a?.replays ?? null)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

export default ReelDetailPage
